import { Button, DataTable, Drawer, EmptyState, Icon, Tag } from '@dayon/design-system';
import { historyColumns, SectionLabel } from './columns.tsx';
import { relative, scopeLabel, stamp } from './format.ts';
import { hasFactor, STATUS, TWO_FACTOR } from './vocabulary.ts';
import { SessionsPanel } from './SessionsPanel.tsx';
import type {
  ActionKind,
  ActionsPayload,
  AdminRow,
  AttemptRow,
  SessionsPayload,
} from './types.ts';

/**
 * The admin detail drawer.
 *
 * Six controls, and they are no longer three live and three dead — every one of
 * them now posts to a route. What closes a control is a RULE rather than a
 * missing endpoint, and each rule is stated where the control is rather than
 * only in a confirmation the operator has not opened yet:
 *
 *   · your own account   — nobody administers their own authority
 *   · the last Super Admin — the platform must keep somebody who can run it
 *   · an outstanding invite — no password, so no session to end and nothing to
 *                             suspend; and no factor, so nothing to reset
 *   · no enrolment yet    — Reset 2FA has nothing to reset
 */
export function AdminDrawer({
  admin,
  onClose,
  canManage,
  canExport,
  saving,
  isSelf,
  isLastSuper,
  roleNeedsFactorRoles,
  history,
  attemptsFailed,
  sessions,
  sessionsLoading,
  sessionsFailed,
  actions,
  actionsLoading,
  actionsFailed,
  onAction,
  onInviteLink,
}: {
  admin: AdminRow | null;
  onClose: () => void;
  canManage: boolean;
  canExport: boolean;
  saving: boolean;
  isSelf: boolean;
  isLastSuper: boolean;
  /** Roles that carry a capability the platform honours only with a factor. */
  roleNeedsFactorRoles: Set<string>;
  history: AttemptRow[];
  attemptsFailed: boolean;
  sessions: SessionsPayload | null;
  sessionsLoading: boolean;
  sessionsFailed: boolean;
  actions: ActionsPayload | null;
  actionsLoading: boolean;
  actionsFailed: boolean;
  onAction: (kind: ActionKind) => void;
  onInviteLink: () => void;
}) {
  const invited = admin?.status === 'INVITED';
  const factor = admin ? TWO_FACTOR[admin.two_factor] : undefined;

  return (
    <Drawer
      open={admin !== null}
      onClose={onClose}
      title={admin?.display_name ?? ''}
      subtitle={
        admin ? (
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <Tag tone={admin.is_super ? 'accent' : 'neutral'}>{admin.role}</Tag>
            <Tag tone={STATUS[admin.status]?.tone ?? 'neutral'}>
              {STATUS[admin.status]?.label ?? admin.status.toLowerCase()}
            </Tag>
            <span>
              {admin.email} · 2FA {factor?.label ?? admin.two_factor} · active{' '}
              {admin.last_sign_in_at ? relative(admin.last_sign_in_at) : 'never'}
            </span>
          </div>
        ) : null
      }
      actions={
        <>
          {invited ? (
            /* An INVITED row has no factor and no session, so the two controls
               that act on those are replaced by the one that moves the invite
               along. It produces a link; it does not send one. */
            <Button onClick={onInviteLink} disabled={!canManage || saving}>
              <Icon name="link" />
              Invite link
            </Button>
          ) : (
            <>
              <Button
                onClick={() => onAction('reset2fa')}
                disabled={!canManage || saving || isSelf || !hasFactor(admin?.two_factor ?? '')}
                {...(admin && !hasFactor(admin.two_factor)
                  ? { 'aria-describedby': 'access-factor-note' }
                  : {})}
              >
                Reset 2FA
              </Button>
              <Button
                onClick={() => onAction('signout')}
                disabled={!canManage || saving || isSelf}
              >
                Sign out all sessions
              </Button>
            </>
          )}
          <Button
            onClick={() => onAction('role')}
            disabled={!canManage || saving || isSelf || isLastSuper}
          >
            Change role
          </Button>
          <Button onClick={() => onAction('scope')} disabled={!canManage || saving || isSelf}>
            Brand scope
          </Button>
          {admin?.status === 'SUSPENDED' ? (
            <Button
              variant="primary"
              onClick={() => onAction('reinstate')}
              disabled={!canManage || saving}
            >
              Reinstate account
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => onAction('suspend')}
              disabled={!canManage || saving || isSelf || isLastSuper || admin?.status !== 'ACTIVE'}
            >
              Suspend account
            </Button>
          )}
        </>
      }
    >
      {admin && (
        <div className="stack">
          <dl className="stack" style={{ margin: 0 }}>
            <div className="def-row">
              <dt>Role</dt>
              <dd>{admin.role}</dd>
            </div>
            <div className="def-row">
              <dt>Brand scope</dt>
              <dd>{scopeLabel(admin.brands)}</dd>
            </div>
            <div className="def-row">
              <dt>Second factor</dt>
              <dd>
                {factor?.detail ?? admin.two_factor}
                {roleNeedsFactorRoles.has(admin.role) && !hasFactor(admin.two_factor)
                  ? ' · required by this role'
                  : ''}
              </dd>
            </div>
            <div className="def-row">
              <dt>Last sign-in</dt>
              <dd>
                {admin.last_sign_in_at
                  ? `${stamp(admin.last_sign_in_at)}${admin.last_sign_in_label ? ` · ${admin.last_sign_in_label}` : ''}`
                  : 'never'}
              </dd>
            </div>
            {admin.invite_expires_at && (
              <div className="def-row">
                <dt>Invite expires</dt>
                <dd>{stamp(admin.invite_expires_at)}</dd>
              </div>
            )}
          </dl>

          {/* Suspension has consequences beyond this admin — the maker-checker
              rule is why. Stated here, where the button is.

              The mechanism, for whoever reads the source rather than the screen:
              suspending increments identity.admin_user.token_version, and that
              number is compared on every request, so each session this admin
              holds stops working at their very next action instead of lingering
              until its token would have expired on its own. The same write
              revokes identity.refresh_token and identity.device_trust for them.
              A column name is nothing an operator weighing up a suspension can
              act on, so the notice promises the EFFECT and the identifiers stay
              in this comment, where they are what a developer actually needs. */}
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              Suspending ends every session this admin holds the moment they next do anything,
              rather than whenever their sign-in would have expired on its own, and it also
              cancels the tokens that keep them signed in between visits and the devices they had
              marked as trusted. Reinstating does not hand those sessions back: they sign in
              afresh. Any items where they are the maker stay pending a different checker. A role
              change and a brand-scope change need no sign-out at all — both are re-checked on
              every action they take.
            </div>
          </div>

          {/* "Pending setup" is a half-finished enrolment: identity.totp_secret
              holds a row for this admin and nothing has ever been confirmed
              against it. The distinction the operator needs is that the
              enrolment is the ADMIN'S to finish and that Reset 2FA would only
              put them back in this same state, which is what the notice says.
              The table name belongs here instead, so that whoever maintains
              this can see which data the claim is read from. */}
          {admin.two_factor === 'Pending setup' && (
            <div className="notice notice-quiet" id="access-factor-note">
              <Icon name="info" />
              <div>
                An enrolment is <strong>outstanding</strong>: {admin.display_name} has started
                setting up an authenticator app but has never confirmed a code from it, which is
                where a factor reset leaves somebody. They complete it themselves at their next
                sign-in by scanning the code the console shows them — no secret is sent anywhere,
                which is why Reset 2FA is closed until this one is finished.
                {roleNeedsFactorRoles.has(admin.role)
                  ? ' Their role mandates a second factor, so sign-in will not complete until they do.'
                  : ''}
              </div>
            </div>
          )}
          {admin.two_factor === 'None' && !invited && (
            <div className="notice notice-quiet" id="access-factor-note">
              <Icon name="info" />
              <div>
                {admin.display_name} has no authenticator enrolled at all, so there is nothing to
                reset — the control is closed rather than doing nothing.
                {roleNeedsFactorRoles.has(admin.role)
                  ? ' Their role mandates a second factor, so sign-in issues them an enrolment instead of a session.'
                  : ' Their role does not mandate one, so they sign in with a password alone and are refused every capability that needs a factor.'}
              </div>
            </div>
          )}

          {/* The rules that close a live control, said where the control is. */}
          {isSelf && (
            <div className="notice notice-quiet">
              <Icon name="lock-simple" />
              <div>
                This is your own account, so every control that changes your authority is closed to
                you — role, brand scope, suspension and the second factor. Nobody administers their
                own authority; it is the same separation that stops the admin who raises a payout
                from approving it. To end your own sessions, use{' '}
                <strong>sign out everywhere</strong> on the account menu — it clears this
                browser&rsquo;s cookie too, which doing it from here would not.
              </div>
            </div>
          )}
          {isLastSuper && (
            <div className="notice notice-quiet">
              <Icon name="lock-simple" />
              <div>
                {admin.display_name} is the last active Super Admin. Suspending or demoting them
                would leave the platform with nobody who can administer it — including nobody who
                could undo it — so the platform refuses both, whoever asks. Give another admin the
                Super Admin role first.
              </div>
            </div>
          )}
          {invited && (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                This invite is outstanding: the account has no password and cannot sign in, so
                there is no session to end and no factor to reset. It lapses on its own
                {admin.invite_expires_at ? ` on ${stamp(admin.invite_expires_at)}` : ''}.
                <strong> Invite link</strong> produces the single-use link that turns this row
                into an active admin, and records the message that carries it. The link is shown
                to you either way, because whether that message is DELIVERED depends on how this
                environment is configured for mail — the dialog says which.
              </div>
            </div>
          )}

          <SessionsPanel
            sessions={sessions}
            loading={sessionsLoading}
            failed={sessionsFailed}
            actions={actions}
            actionsLoading={actionsLoading}
            actionsFailed={actionsFailed}
            canReadActions={canExport}
          />

          <SectionLabel>LOGIN HISTORY</SectionLabel>
          <DataTable
            caption={`Sign-in attempts for ${admin.display_name}, newest first`}
            columns={historyColumns}
            rows={history}
            rowKey={(a) => `${a.created_at}-${a.stage}`}
            empty={
              // Derived from the same trail, so it inherits the same three
              // states: unread, withheld, and genuinely nothing for this admin.
              // Only the last of them is a fact about the admin.
              //
              // That third state is narrower than it looks, and the wording has
              // to stay honest about it: the trail is identity.auth_attempt,
              // served by GET /api/access/attempts, which takes neither an
              // admin filter nor a page cursor, so the console only ever holds
              // the newest 100 rows platform-wide. "None of those belongs to
              // this admin" is therefore all we can truthfully claim — never
              // "this admin has never signed in". The operator is told the
              // consequence; the table and the two missing parameters live here
              // because they are what somebody would need in order to lift the
              // limit rather than reword the notice.
              <EmptyState
                icon={attemptsFailed ? 'warning-circle' : 'clock-counter-clockwise'}
                title={attemptsFailed ? 'The trail could not be read' : 'Nothing in the window we hold'}
              >
                {attemptsFailed
                  ? 'The sign-in trail did not load, so this history is empty because nothing was read — not because this admin has never signed in and not because they have never been targeted.'
                  : canExport
                    ? 'This was read successfully, and there is genuinely nothing here for this admin: the console holds the 100 most recent sign-in attempts for the whole platform, and none of them is theirs. Older attempts than those are still kept — this view simply cannot ask for one admin on their own, or look back past the most recent 100, so it does not mean they have never signed in.'
                    : 'Reading the sign-in trail needs audit.export, which your role does not hold.'}
              </EmptyState>
            }
          />
          {/* Why the designed counter is absent rather than approximated:
              identity.auth_attempt stores the OUTCOME of an attempt and not its
              position in the run of failures that leads to a lock-out, and the
              window this drawer can see is a partial one anyway, so counting
              the rows in hand would produce a confidently wrong "2 of 5". A
              missing counter is honest; a wrong one would be read as fact. */}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            There is no failure counter such as &ldquo;Wrong password · 1 of 5&rdquo;. What is kept
            records how each attempt ended but not which attempt it was in the run-up to a
            lock-out, so the counter is left off rather than guessed from the part of the history
            this drawer can see.
          </div>
        </div>
      )}
    </Drawer>
  );
}
